import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";

const API_BASE =
  (process.env.REACT_APP_API_URL || "").includes("undefined")
    ? "http://localhost:8000/api"
    : (process.env.REACT_APP_API_URL || "http://localhost:8000/api");

export default function ShiftHandover({ patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [createHandoverOpen, setCreateHandoverOpen] = useState(false);

  const {
    data: handoversData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["shift-handovers", patient?.id],
    queryFn: () => {
      let url = `${API_BASE}/shift-handovers/`;
      const params = new URLSearchParams();
      if (patient?.id) params.append("patient", patient.id);
      if (params.toString()) url += `?${params.toString()}`;
      
      return axios
        .get(url, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => res.data);
    },
    enabled: !!token && !!patient?.id,
  });

  const handovers = handoversData?.results || handoversData || [];

  if (!patient) {
    return (
      <Alert severity="info">
        {t("nurse.selectPatientForHandover", "Please select a patient to view shift handovers.")}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", borderRadius: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">
          {t("nurse.shiftHandover", "Shift Handover")}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateHandoverOpen(true)}
        >
          {t("nurse.createHandover", "Create Handover")}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {error?.message || t("nurse.errors.loadFailed", "Failed to load shift handovers.")}
        </Alert>
      ) : handovers.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setCreateHandoverOpen(true)}>
              {t("nurse.createOne", "Create One")}
            </Button>
          }
        >
          {t("nurse.noHandovers", "No shift handovers found for this patient.")}
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {handovers.map((handover) => (
            <Card key={handover.id} variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6">
                      {moment(handover.handover_date).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("nurse.from", "From")}: {handover.from_nurse_display} → {t("nurse.to", "To")}: {handover.to_nurse_display}
                    </Typography>
                    {handover.shift_type && (
                      <Chip
                        label={handover.shift_type_display}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    {t("nurse.patientStatus", "Patient Status")}:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {handover.patient_status}
                  </Typography>
                </Box>

                {handover.outstanding_tasks && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {t("nurse.outstandingTasks", "Outstanding Tasks")}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {handover.outstanding_tasks}
                    </Typography>
                  </Box>
                )}

                {handover.critical_alerts && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="warning">
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {t("nurse.criticalAlerts", "Critical Alerts")}:
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {handover.critical_alerts}
                      </Typography>
                    </Alert>
                  </Box>
                )}

                {handover.medications_due && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {t("nurse.medicationsDue", "Medications Due")}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {handover.medications_due}
                    </Typography>
                  </Box>
                )}

                {handover.special_instructions && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {t("nurse.specialInstructions", "Special Instructions")}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {handover.special_instructions}
                    </Typography>
                  </Box>
                )}

                {handover.family_communication && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      {t("nurse.familyCommunication", "Family Communication")}:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {handover.family_communication}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <ShiftHandoverCreate
        open={createHandoverOpen}
        onClose={() => {
          setCreateHandoverOpen(false);
          queryClient.invalidateQueries(["shift-handovers"]);
        }}
        patient={patient}
        encounter={encounter}
      />
    </Box>
  );
}

function ShiftHandoverCreate({ open, onClose, patient, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    patient: patient?.id || "",
    encounter: encounter?.id || "",
    to_nurse: "",
    shift_type: "",
    patient_status: "",
    outstanding_tasks: "",
    critical_alerts: "",
    medications_due: "",
    special_instructions: "",
    family_communication: "",
  });
  const [errors, setErrors] = useState({});

  const { data: nursesData } = useQuery({
    queryKey: ["nurses"],
    queryFn: () =>
      axios
        .get(`${API_BASE}/users/?page_size=1000`, {
          headers: { Authorization: `Token ${token}` },
        })
        .then((res) => {
          // Filter to show nurses OR admins
          const allUsers = res.data?.results || res.data || [];
          const filtered = allUsers.filter(
            (user) => user.nurse === true || user.is_admin === true
          );
          return { ...res.data, results: filtered };
        }),
    enabled: !!token,
  });

  const nurses = nursesData?.results || nursesData || [];

  React.useEffect(() => {
    if (open) {
      setFormData({
        patient: patient?.id || "",
        encounter: encounter?.id || "",
        to_nurse: "",
        shift_type: "",
        patient_status: "",
        outstanding_tasks: "",
        critical_alerts: "",
        medications_due: "",
        special_instructions: "",
        family_communication: "",
      });
      setErrors({});
    }
  }, [open, patient, encounter]);

  const createHandover = useMutation({
    mutationFn: (data) =>
      axios.post(`${API_BASE}/shift-handovers/`, data, {
        headers: { Authorization: `Token ${token}` },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(["shift-handovers"]);
      onClose();
    },
    onError: (error) => {
      console.error("Failed to create shift handover:", error);
      setErrors(error.response?.data || { non_field_errors: "Failed to create handover." });
    },
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.patient) {
      newErrors.patient = t("nurse.errors.patientRequired", "Patient is required.");
    }
    if (!formData.to_nurse) {
      newErrors.to_nurse = t("nurse.errors.toNurseRequired", "Receiving nurse is required.");
    }
    if (!formData.patient_status.trim()) {
      newErrors.patient_status = t("nurse.errors.patientStatusRequired", "Patient status is required.");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      ...formData,
      from_nurse: auth?.user?.user?.id,
    };
    createHandover.mutate(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("nurse.createShiftHandover", "Create Shift Handover")}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {createHandover.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.non_field_errors || createHandover.error?.message || t("nurse.errors.genericError", "An error occurred.")}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense" error={!!errors.to_nurse}>
                <InputLabel id="to-nurse-label">{t("nurse.toNurse", "To Nurse")}</InputLabel>
                <Select
                  labelId="to-nurse-label"
                  id="to-nurse-select"
                  value={formData.to_nurse}
                  label={t("nurse.toNurse", "To Nurse")}
                  onChange={handleChange("to_nurse")}
                >
                  {nurses.filter(n => n.id !== auth?.user?.user?.id).map((nurse) => (
                    <MenuItem key={nurse.id} value={nurse.id}>
                      {nurse.first_name} {nurse.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.to_nurse && <Typography variant="caption" color="error">{errors.to_nurse}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="shift-type-label">{t("nurse.shiftType", "Shift Type")}</InputLabel>
                <Select
                  labelId="shift-type-label"
                  id="shift-type-select"
                  value={formData.shift_type}
                  label={t("nurse.shiftType", "Shift Type")}
                  onChange={handleChange("shift_type")}
                >
                  <MenuItem value="morning">{t("nurse.shiftMorning", "Morning")}</MenuItem>
                  <MenuItem value="evening">{t("nurse.shiftEvening", "Evening")}</MenuItem>
                  <MenuItem value="night">{t("nurse.shiftNight", "Night")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={`${t("nurse.patientStatus", "Patient Status")} *`}
                value={formData.patient_status}
                onChange={handleChange("patient_status")}
                error={!!errors.patient_status}
                helperText={errors.patient_status}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.outstandingTasks", "Outstanding Tasks (Optional)")}
                value={formData.outstanding_tasks}
                onChange={handleChange("outstanding_tasks")}
                error={!!errors.outstanding_tasks}
                helperText={errors.outstanding_tasks}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.criticalAlerts", "Critical Alerts (Optional)")}
                value={formData.critical_alerts}
                onChange={handleChange("critical_alerts")}
                error={!!errors.critical_alerts}
                helperText={errors.critical_alerts}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.medicationsDue", "Medications Due Next Shift (Optional)")}
                value={formData.medications_due}
                onChange={handleChange("medications_due")}
                error={!!errors.medications_due}
                helperText={errors.medications_due}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.specialInstructions", "Special Instructions (Optional)")}
                value={formData.special_instructions}
                onChange={handleChange("special_instructions")}
                error={!!errors.special_instructions}
                helperText={errors.special_instructions}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("nurse.familyCommunication", "Family Communication Notes (Optional)")}
                value={formData.family_communication}
                onChange={handleChange("family_communication")}
                error={!!errors.family_communication}
                helperText={errors.family_communication}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={createHandover.isPending}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createHandover.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={createHandover.isPending || !patient}
          >
            {createHandover.isPending ? t("common.saving", "Saving...") : t("common.save", "Save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
